'use client';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon, type FontAwesomeIconProps} from '@fortawesome/react-fontawesome';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import {DIRECTIONAL_GLYPHS} from './directional';
import {mirrorRtl} from './mirror-rtl';

// FA's own props carry no `sx`, but the glyph renders through a MUI `styled()`
// component, which does consume it. Declaring it keeps theme-aware styling
// (`color: theme => …`, responsive fontSize) available on every glyph.
export type AppIconProps = Omit<FontAwesomeIconProps, 'icon'> & {
  icon: IconDefinition;
  sx?: SxProps<Theme>;
};

// The flat member of the `sx` array-merge union — `SxProps<Theme>` allows
// *being* an array, so `SxProps<Theme>[]` (array of arrays) is the wrong shape.
type SxArrayEntry = Extract<SxProps<Theme>, readonly unknown[]>[number];

// `Array.isArray` is typed `(arg: any) => arg is any[]` in lib.d.ts, which
// poisons the narrowed type; this generic re-declaration keeps it concrete.
function isReadonlyArray<T>(value: T | readonly T[]): value is readonly T[] {
  return Array.isArray(value);
}

const MirrorableFaIcon = styled(FontAwesomeIcon, {shouldForwardProp: propName => propName !== 'oMirror'})<{oMirror: boolean}>(
  ({oMirror}) => (oMirror ? mirrorRtl : undefined),
);

export const DEFAULT_ICON_SIZE = 16;

// FontAwesome sizes by `height: 1em`, so a glyph with nothing specified silently takes
// whatever font-size it inherits. This gives the catalog one predictable default instead.
//
// It is applied through `sx` rather than a styled base so that a caller's own `sx`
// (appended after it) still wins. Callers who size via the `fontSize`/`size` *attributes*
// opt out entirely: those land on the SVG as presentation attributes, which lose to any
// CSS rule, so a base font-size would silently override `fontSize="inherit"`.
function sizingIsExplicit(props: {fontSize?: unknown; size?: unknown; style?: {fontSize?: unknown}}): boolean {
  return props.fontSize !== undefined || props.size !== undefined || props.style?.fontSize !== undefined;
}

export function AppIcon(props: AppIconProps) {
  const {icon, sx, ...rest} = props;

  const sxArray: SxArrayEntry[] = [];
  if (!sizingIsExplicit(props)) {
    sxArray.push({fontSize: DEFAULT_ICON_SIZE});
  }
  if (isReadonlyArray(sx)) {
    sxArray.push(...sx);
  } else if (sx) {
    sxArray.push(sx);
  }

  return <MirrorableFaIcon oMirror={DIRECTIONAL_GLYPHS.has(icon)} icon={icon} sx={sxArray} {...rest} />;
}

AppIcon.displayName = 'AppIcon';
